import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { HitlInterrupt } from '@/store/chatStore';
import { ShieldCheck, Check, Loader2, GraduationCap, ExternalLink } from 'lucide-react';

interface HitlApprovalMessageProps {
    interrupt: HitlInterrupt;
    onDecision: (decision: 'approve' | 'reject', message?: string) => void;
}

/**
 * Obtiene la información descriptiva de la acción según el tipo de herramienta
 */
function getActionInfo(toolName: string): {
    title: string;
} {
    const actionInfo: Record<string, { title: string }> = {
        'inscribir_usuario': {
            title: 'Confirmar inscripción',
        },
        'search_learning_data': {
            title: 'Búsqueda de cursos',
        },
        'search_business_knowledge': {
            title: 'Consulta de información',
        },
    };

    const info = actionInfo[toolName] || {
        title: toolName,
    };

    return {
        title: info.title,
    };
}

/**
 * Componente de mensaje de aprobación HITL que se muestra como parte del chat
 */
export function HitlApprovalMessage({ interrupt, onDecision }: HitlApprovalMessageProps) {
    const [isProcessing, setIsProcessing] = useState(false);
    const [selectedDecision, setSelectedDecision] = useState<'approve' | 'reject' | null>(null);
    const [acceptedTerms, setAcceptedTerms] = useState(true);

    const handleApprove = () => {
        if (!acceptedTerms) return;
        setIsProcessing(true);
        setSelectedDecision('approve');
        onDecision('approve');
    };

    const handleReject = () => {
        setIsProcessing(true);
        setSelectedDecision('reject');
        onDecision('reject', 'El usuario rechazó la acción');
    };

    const actionInfo = getActionInfo(interrupt.toolName);

    // Usar la descripción del backend si está disponible, sino usar fallback
    const description = interrupt.description || 'Necesito tu autorización para continuar con esta acción.';

    return (
        <div className="flex w-full justify-start animate-in fade-in-50 slide-in-from-bottom-2 duration-300 my-2">
            <Card className="w-full max-w-[95%] shadow-sm border bg-card overflow-hidden">
                <div className="p-4 space-y-4">
                    {/* Header & Description */}
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5 p-1.5 bg-primary/10 rounded-full">
                            <ShieldCheck className="w-4 h-4 text-primary" />
                        </div>
                        <div className="space-y-1">
                            <h4 className="text-sm font-medium text-foreground leading-none">
                                {actionInfo.title}
                            </h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {description}
                            </p>
                        </div>
                    </div>

                    {/* Course Info - Compact Horizontal Card */}
                    {interrupt.courseInfo && (
                        <div className="flex items-center gap-3 p-3 rounded-md bg-muted/40 border border-border/50">
                            <div className="flex-shrink-0 p-2 bg-background rounded-md border shadow-sm">
                                <GraduationCap className="w-4 h-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0 space-y-1">
                                <p className="text-sm font-medium text-foreground truncate">
                                    {interrupt.courseInfo.courseName}
                                </p>
                                {interrupt.courseInfo.description && (
                                    <p className="text-xs text-muted-foreground line-clamp-2">
                                        {interrupt.courseInfo.description}
                                    </p>
                                )}
                            </div>
                            {interrupt.courseInfo.url && interrupt.courseInfo.rootPath && (
                                <Button variant="link" size="sm" className="h-auto px-0 text-xs shrink-0 gap-1 text-primary" asChild>
                                    <a
                                        href={`${interrupt.courseInfo.rootPath}${interrupt.courseInfo.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Ver detalles del curso"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <span>Ver</span>
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                </Button>
                            )}
                        </div>
                    )}

                    {/* Footer: Terms & Actions */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2 border-t border-border/40">
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id={`terms-${interrupt.id}`}
                                checked={acceptedTerms}
                                onChange={(e) => setAcceptedTerms(e.target.checked)}
                                className="w-3.5 h-3.5 rounded border-primary/30 text-primary focus:ring-primary cursor-pointer"
                            />
                            <label
                                htmlFor={`terms-${interrupt.id}`}
                                className="text-xs text-muted-foreground cursor-pointer select-none"
                            >
                                <a
                                    href="https://tyc.utn.centrodeelearning.com/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary hover:underline font-medium"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Acepto condiciones de compra
                                </a>
                            </label>
                        </div>

                        <div className="flex items-center gap-2 self-end sm:self-auto">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleReject}
                                disabled={isProcessing}
                                className="h-8 px-3 text-xs font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            >
                                {isProcessing && selectedDecision === 'reject' && (
                                    <Loader2 className="mr-2 w-3 h-3 animate-spin" />
                                )}
                                Cancelar
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleApprove}
                                disabled={isProcessing || !acceptedTerms}
                                className="h-8 px-4 text-xs font-medium bg-primary text-primary-foreground shadow-sm hover:bg-primary/90"
                            >
                                {isProcessing && selectedDecision === 'approve' ? (
                                    <Loader2 className="mr-2 w-3 h-3 animate-spin" />
                                ) : (
                                    <Check className="mr-2 w-3 h-3" />
                                )}
                                Confirmar
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
}

export default HitlApprovalMessage;
